import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Bot,
    CheckCircle2,
    FileUp,
    Lightbulb,
    Loader2,
    Plus,
    ScrollText,
    Trash2,
    Trophy,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { C } from "@/data/content";
import Reveal, { SectionHeader } from "@/components/Reveal";
import { scrollToId } from "@/utils/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_SUP = {
    name: "",
    unit_price: "",
    moq: "",
    lead_time_days: "",
    sample_quality: "3",
    certifications: "",
};

function fallbackAnalysis(isAr, suppliers) {
    const nums = suppliers.map((s) => ({
        price: parseFloat(s.unit_price) || Infinity,
        lead: parseInt(s.lead_time_days) || Infinity,
        moq: parseInt(s.moq) || 0,
        quality: parseInt(s.sample_quality) || 3,
    }));
    const minPrice = Math.min(...nums.map((n) => n.price));
    const minLead = Math.min(...nums.map((n) => n.lead));
    const minMoq = Math.min(...nums.filter((n) => n.moq > 0).map((n) => n.moq), Infinity);

    const scored = suppliers.map((s, i) => {
        const n = nums[i];
        const score = Math.round(
            (n.quality / 5) * 40 +
                (n.price !== Infinity && minPrice !== Infinity ? Math.max(0.5, minPrice / n.price) * 30 : 12) +
                (n.lead !== Infinity && minLead !== Infinity ? (minLead / n.lead) * 15 : 6) +
                (n.moq > 0 && minMoq !== Infinity ? (minMoq / n.moq) * 10 : 10) +
                (s.certifications ? 5 : 0)
        );
        return { ...s, score: Math.min(100, score) };
    });
    const winner = scored.reduce((a, b) => (b.score > a.score ? b : a), scored[0]);
    const wn = nums[suppliers.indexOf(winner)];
    const isArReason = isAr;
    return {
        suppliers: scored,
        winner: winner.name,
        winner_reason: isArReason
            ? `سبب اختيار ${winner.name}: جودة العينة ${wn.quality}/5 وهي الأعلى بين العروض المدخلة، وسعر الوحدة ${wn.price === Infinity ? "غير محدد" : `$${wn.price}`} وهو ضمن النطاق المناسب مقارنة بالبديل${wn.certifications ? `، مع شهادات معتمدة (${wn.certifications}) تغطي متطلبات الأسواق الرئيسية` : ""}${wn.lead !== Infinity ? `، ومدة تسليم ${wn.lead} يومًا` : ""}. فارق السعر مع البديل الأرخص لا يعوّض انخفاض الجودة وتأثيرها على رضا عملائك ومعدلات الإرجاع. لذلك فهو يحقق التوازن الأمثل: جودة ممتازة بسعر مناسب.`
            : `Why ${winner.name}: sample quality ${wn.quality}/5 — the highest among the quotes entered — with a unit price of ${wn.price === Infinity ? "n/a" : `$${wn.price}`} that sits within a reasonable range versus the alternative${wn.certifications ? `, plus certified compliance (${wn.certifications}) covering key market requirements` : ""}${wn.lead !== Infinity ? `, and a ${wn.lead}-day lead time` : ""}. The price gap with the cheapest option does not compensate for the quality drop and its impact on customer satisfaction and return rates. It therefore strikes the optimal balance: excellent quality at a suitable price.`,
        recommendation: isAr
            ? `بناءً على الأرقام المدخلة، يقدّم ${winner.name} جودةً ممتازة عند سعرٍ مناسب — وهي الأولوية الأولى في الاختيار. ننصح بطلب عينة للتقييم الفني ومقارنة عرض نهائي شامل الشحن.`
            : `Based on the figures entered, ${winner.name} offers excellent quality at a suitable price — quality being the top priority. We recommend requesting a sample for technical evaluation and a final all-in quote.`,
        negotiation_tips: isAr
            ? [
                  "اطلب عينة دائمًا قبل أي التزام — الجودة أولًا، ولا تقايضها بفرق سعر بسيط.",
                  "قارن السعر شامل الشحن (FOB مقابل CIF) لا السعر وحده.",
                  "اطلب تأكيد الشهادات المطلوبة لسوقك كتابيًا.",
              ]
            : [
                  "Always request a sample before committing — quality first, never traded for a small price gap.",
                  "Compare landed cost (FOB vs CIF), not unit price alone.",
                  "Get written confirmation of the certifications your market requires.",
              ],
    };
}

export default function VendorCompare() {
    const { L, lang } = useLanguage();
    const T = C.compare;
    const [product, setProduct] = useState("");
    const [quantity, setQuantity] = useState("");
    const [specs, setSpecs] = useState("");
    const [suppliers, setSuppliers] = useState([{ ...EMPTY_SUP }, { ...EMPTY_SUP }]);
    const [analyzing, setAnalyzing] = useState(false);
    const [readingRow, setReadingRow] = useState(null);
    const [result, setResult] = useState(null);
    const [usedFallback, setUsedFallback] = useState(false);
    const [lastInput, setLastInput] = useState([]);

    const setSup = (i, key, val) => {
        const next = [...suppliers];
        next[i] = { ...next[i], [key]: val };
        setSuppliers(next);
    };

    const readQuote = async (i, file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error(L(T.fileHint));
            return;
        }
        setReadingRow(i);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/vendor-compare/extract`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const ex = res.data.extracted;
            setSuppliers((prev) =>
                prev.map((s, j) =>
                    j !== i
                        ? s
                        : {
                              ...s,
                              name: s.name || ex.name || "",
                              unit_price: ex.unit_price ? String(ex.unit_price) : s.unit_price,
                              moq: ex.moq ? String(ex.moq) : s.moq,
                              lead_time_days: ex.lead_time_days ? String(ex.lead_time_days) : s.lead_time_days,
                              sample_quality: ex.sample_quality ? String(ex.sample_quality) : s.sample_quality,
                              certifications: s.certifications || ex.certifications || "",
                          }
                )
            );
            toast.success(L(T.readOk));
        } catch {
            toast.error(L(T.readFail));
        } finally {
            setReadingRow(null);
        }
    };

    const submit = async () => {
        const valid = suppliers.filter((s) => s.name.trim() && s.unit_price !== "");
        if (valid.length < 2) {
            toast.error(L(T.minError));
            return;
        }
        setAnalyzing(true);
        try {
            const res = await axios.post(`${API}/vendor-compare`, {
                product: product || L(T.productPh),
                quantity: parseInt(quantity) || 0,
                specs: specs || null,
                lang,
                suppliers: valid.map((s) => ({
                    name: s.name.trim(),
                    unit_price: parseFloat(s.unit_price) || 0,
                    moq: parseInt(s.moq) || 0,
                    lead_time_days: parseInt(s.lead_time_days) || 0,
                    sample_quality: parseInt(s.sample_quality) || 3,
                    certifications: s.certifications || null,
                })),
            });
            setResult(res.data.result);
            setUsedFallback(false);
        } catch {
            setResult(fallbackAnalysis(lang === "ar", valid));
            setUsedFallback(true);
            toast.error(L(T.failError));
        } finally {
            setLastInput(valid);
            setAnalyzing(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-gold-line bg-ink/60 px-3 py-2.5 text-sm text-txt-1 placeholder:text-txt-3 outline-none transition-colors focus:border-gold-line-strong";
    const labelCls = "mb-1.5 block text-xs text-txt-2";

    const fmt = (v) =>
        v === null || v === "" || v === undefined
            ? "—"
            : Number(v).toLocaleString(lang === "ar" ? "ar-EG" : "en-US", { maximumFractionDigits: 2 });

    const statusStyle = {
        match: "border-green-400/60 bg-green-400/10 text-green-300",
        close: "border-amber-400/60 bg-amber-400/10 text-amber-300",
        missing: "border-red-400/60 bg-red-400/10 text-red-300",
        unknown: "border-gold-line-strong bg-ink/60 text-txt-3",
    };

    return (
        <section id="compare" className="py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6">
                <SectionHeader eyebrow={L(T.eyebrow)} title={L(T.title)} align="center" />
                <Reveal delay={0.1}>
                    <p className="mx-auto mt-6 max-w-2xl text-center leading-relaxed text-txt-2">
                        {L(T.text)}
                    </p>
                </Reveal>

                <Reveal delay={0.16} className="mt-12">
                    <div className="glass rounded-2xl border border-gold-line p-6 sm:p-8" data-testid="vendor-compare-form">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label className={labelCls}>{L(T.product)}</label>
                                <input
                                    value={product}
                                    onChange={(e) => setProduct(e.target.value)}
                                    placeholder={L(T.productPh)}
                                    data-testid="compare-product-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{L(T.quantity)}</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    data-testid="compare-quantity-input"
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className={labelCls}>{L(T.specs)}</label>
                            <textarea
                                rows={2}
                                value={specs}
                                onChange={(e) => setSpecs(e.target.value)}
                                placeholder={L(T.specsPh)}
                                data-testid="compare-specs-input"
                                className={`${inputCls} resize-none`}
                            />
                        </div>

                        <p className="font-mono-x mt-8 text-[11px] tracking-[0.25em] text-brass uppercase">
                            {L(T.suppliers)}
                        </p>

                        <div className="mt-4 space-y-4">
                            {suppliers.map((sup, i) => (
                                <div
                                    key={i}
                                    data-testid={`compare-supplier-row-${i + 1}`}
                                    className="relative rounded-xl border border-gold-line/70 bg-ink-2/50 p-4"
                                >
                                    {suppliers.length > 2 && (
                                        <button
                                            onClick={() => setSuppliers(suppliers.filter((_, j) => j !== i))}
                                            data-testid={`compare-remove-${i + 1}`}
                                            aria-label="Remove supplier"
                                            className="absolute end-3 top-3 z-10 grid size-7 place-items-center rounded-full border border-gold-line text-txt-3 transition-colors hover:border-brass hover:text-brass"
                                        >
                                            <Trash2 className="size-3.5" />
                                        </button>
                                    )}
                                    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                                        <div className="sm:col-span-3 lg:col-span-2">
                                            <label className={labelCls}>{L(T.name)}</label>
                                            <input
                                                value={sup.name}
                                                onChange={(e) => setSup(i, "name", e.target.value)}
                                                data-testid={`compare-supplier-name-${i + 1}`}
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>{L(T.price)}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={sup.unit_price}
                                                onChange={(e) => setSup(i, "unit_price", e.target.value)}
                                                data-testid={`compare-supplier-price-${i + 1}`}
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>{L(T.moq)}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={sup.moq}
                                                onChange={(e) => setSup(i, "moq", e.target.value)}
                                                data-testid={`compare-supplier-moq-${i + 1}`}
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>{L(T.lead)}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={sup.lead_time_days}
                                                onChange={(e) => setSup(i, "lead_time_days", e.target.value)}
                                                data-testid={`compare-supplier-lead-${i + 1}`}
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls}>{L(T.quality)}</label>
                                            <select
                                                value={sup.sample_quality}
                                                onChange={(e) => setSup(i, "sample_quality", e.target.value)}
                                                data-testid={`compare-supplier-quality-${i + 1}`}
                                                className={`${inputCls} appearance-none`}
                                            >
                                                {[1, 2, 3, 4, 5].map((q) => (
                                                    <option key={q} value={q}>
                                                        {q}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="sm:col-span-3 lg:col-span-1">
                                            <label className={labelCls}>{L(T.certs)}</label>
                                            <input
                                                value={sup.certifications}
                                                onChange={(e) => setSup(i, "certifications", e.target.value)}
                                                data-testid={`compare-supplier-certs-${i + 1}`}
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-3 border-t border-gold-line/40 pt-3">
                                        <input
                                            id={`compare-file-${i + 1}`}
                                            type="file"
                                            accept="image/*,.pdf"
                                            className="hidden"
                                            onChange={(e) => {
                                                readQuote(i, e.target.files?.[0]);
                                                e.target.value = "";
                                            }}
                                            data-testid={`compare-file-input-${i + 1}`}
                                        />
                                        <label
                                            htmlFor={`compare-file-${i + 1}`}
                                            data-testid={`compare-upload-button-${i + 1}`}
                                            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-brass/60 px-4 py-2 text-xs font-semibold text-brass transition-colors hover:bg-brass/10"
                                        >
                                            {readingRow === i ? (
                                                <>
                                                    <Loader2 className="size-3.5 animate-spin" />
                                                    {L(T.reading)}
                                                </>
                                            ) : sup.name ? (
                                                <>
                                                    <CheckCircle2 className="size-3.5 text-green-400" />
                                                    {L(T.uploadCta)}
                                                </>
                                            ) : (
                                                <>
                                                    <FileUp className="size-3.5" />
                                                    {L(T.uploadCta)}
                                                </>
                                            )}
                                        </label>
                                        <span className="font-mono-x ms-3 text-[10px] text-txt-3" dir="auto">
                                            {L(T.fileHint)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex flex-wrap items-center gap-4">
                            {suppliers.length < 4 && (
                                <button
                                    onClick={() => setSuppliers([...suppliers, { ...EMPTY_SUP }])}
                                    data-testid="compare-add-supplier"
                                    className="flex items-center gap-2 rounded-full border border-dashed border-brass/60 px-5 py-2.5 text-xs font-semibold text-brass transition-colors hover:bg-brass/10"
                                >
                                    <Plus className="size-3.5" />
                                    {L(T.add)}
                                </button>
                            )}
                            <button
                                onClick={submit}
                                disabled={analyzing}
                                data-testid="compare-submit"
                                className="flex items-center gap-2 rounded-full bg-brass px-7 py-3 text-sm font-bold text-ink transition-transform hover:scale-[1.02] disabled:opacity-60"
                            >
                                {analyzing ? (
                                    <>
                                        <Loader2 className="size-4 animate-spin" />
                                        {L(T.analyzing)}
                                    </>
                                ) : (
                                    <>
                                        <Bot className="size-4" />
                                        {L(T.submit)}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Reveal>

                {result && lastInput.length > 0 && (
                    <div className="mt-10" data-testid="compare-result">
                        <Reveal>
                            <div className="flex items-center gap-4 rounded-2xl border border-gold-line-strong bg-brass/10 p-6">
                                <Trophy className="size-8 shrink-0 text-brass" />
                                <div>
                                    <p className="font-mono-x text-[10px] tracking-[0.3em] text-brass uppercase">
                                        {L(T.winner)}
                                    </p>
                                    <p className="font-display text-2xl font-bold text-txt-1" data-testid="compare-result-winner" dir="auto">
                                        {result.winner}
                                    </p>
                                </div>
                            </div>
                        </Reveal>

                        {result.winner_reason && (
                            <Reveal delay={0.06}>
                                <div className="mt-6 rounded-2xl border border-gold-line-strong bg-ink-3/60 p-6" data-testid="compare-winner-reason">
                                    <p className="flex items-center gap-2 font-mono-x text-[10px] tracking-[0.3em] text-brass uppercase">
                                        <ScrollText className="size-3.5" />
                                        {L(T.winnerReason)}
                                    </p>
                                    <p className="mt-3 leading-loose text-txt-1" dir="auto">
                                        {result.winner_reason}
                                    </p>
                                </div>
                            </Reveal>
                        )}

                        <Reveal delay={0.08}>
                            <p className="font-mono-x mt-8 text-[11px] tracking-[0.25em] text-brass uppercase">
                                {L(T.tableTitle)}
                            </p>
                            <div className="mt-3 overflow-x-auto rounded-2xl border border-gold-line" data-testid="compare-result-table">
                                <table className="w-full min-w-[820px] text-sm" dir={lang === "ar" ? "rtl" : "ltr"}>
                                    <thead>
                                        <tr className="bg-ink-3/80 text-start text-xs text-txt-2">
                                            {[L(T.name), L(T.price), L(T.total), L(T.moq), L(T.lead), L(T.quality), L(T.certs), L({ ar: "الدرجة", en: "Score" })].map(
                                                (h, i) => (
                                                    <th key={i} className="whitespace-nowrap px-4 py-3 text-start font-semibold">
                                                        {h}
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lastInput.map((s, i) => {
                                            const ai =
                                                result.suppliers.find((x) => (x.name || "").trim() === s.name.trim()) ||
                                                result.suppliers[i] ||
                                                {};
                                            const qty = parseInt(quantity) || 0;
                                            const price = parseFloat(s.unit_price) || 0;
                                            const total = qty > 0 && price > 0 ? price * qty : null;
                                            const isWinner = (result.winner || "").trim() === s.name.trim();
                                            return (
                                                <tr
                                                    key={i}
                                                    data-testid={`compare-detail-row-${i + 1}`}
                                                    className={`border-t border-gold-line/30 ${isWinner ? "bg-brass/5" : i % 2 === 0 ? "bg-ink-2/60" : "bg-ink-3/30"}`}
                                                >
                                                    <td className="px-4 py-3 font-semibold text-txt-1" dir="auto">
                                                        {isWinner && <Trophy className="me-1 inline size-3.5 text-brass" />}
                                                        {s.name}
                                                    </td>
                                                    <td className="px-4 py-3 text-txt-2" dir="ltr">${fmt(price)}</td>
                                                    <td className="px-4 py-3 text-txt-2" dir="ltr">{total ? `$${fmt(total)}` : "—"}</td>
                                                    <td className="px-4 py-3 text-txt-2">{fmt(s.moq)}</td>
                                                    <td className="px-4 py-3 text-txt-2">{s.lead_time_days ? fmt(s.lead_time_days) : "—"}</td>
                                                    <td className="px-4 py-3 text-txt-2">{s.sample_quality}/5</td>
                                                    <td className="px-4 py-3 text-txt-2" dir="auto">{s.certifications || "—"}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-ink/70">
                                                                <div
                                                                    className="h-full rounded-full bg-gradient-to-r from-brass-muted to-brass"
                                                                    style={{ width: `${ai.score || 0}%` }}
                                                                />
                                                            </div>
                                                            <span className="font-mono-x text-xs text-brass" data-testid={`compare-score-${i + 1}`}>
                                                                {ai.score ?? "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Reveal>

                        {result.suppliers.map((s, i) => (
                            <Reveal key={i} delay={0.08}>
                                <div className="mt-6 rounded-2xl border border-gold-line bg-ink-2/40 p-6" data-testid={`compare-verdict-${i + 1}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <p className="font-display text-lg font-bold text-txt-1" dir="auto">
                                            {s.name}
                                        </p>
                                        {s.verdict && (
                                            <p className="max-w-2xl text-sm leading-relaxed text-txt-2" dir="auto">
                                                {s.verdict}
                                            </p>
                                        )}
                                    </div>
                                    {s.spec_compliance?.length > 0 && (
                                        <div className="mt-4">
                                            <p className="font-mono-x text-[10px] tracking-[0.3em] text-brass uppercase">
                                                {L(T.specCheck)}
                                            </p>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {s.spec_compliance.map((sp, j) => (
                                                    <span
                                                        key={j}
                                                        data-testid={`compare-spec-${i + 1}-${j + 1}`}
                                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${statusStyle[sp.status] || statusStyle.unknown}`}
                                                    >
                                                        <span dir="auto" className="font-semibold">{sp.spec}</span>
                                                        <span dir="auto">— {L(T.statusLabels[sp.status] || T.statusLabels.unknown)}</span>
                                                        {sp.note && <span dir="auto" className="text-txt-3">{sp.note}</span>}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {(s.pros?.length > 0 || s.cons?.length > 0) && (
                                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                            {s.pros?.length > 0 && (
                                                <div>
                                                    <p className="text-xs font-semibold text-green-400">{L({ ar: "المزايا", en: "Pros" })}</p>
                                                    <ul className="mt-1.5 list-inside list-disc text-xs leading-relaxed text-txt-2">
                                                        {s.pros.map((p, j) => (
                                                            <li key={j} dir="auto">{p}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {s.cons?.length > 0 && (
                                                <div>
                                                    <p className="text-xs font-semibold text-red-400">{L({ ar: "العيوب", en: "Cons" })}</p>
                                                    <ul className="mt-1.5 list-inside list-disc text-xs leading-relaxed text-txt-2">
                                                        {s.cons.map((c, j) => (
                                                            <li key={j} dir="auto">{c}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Reveal>
                        ))}

                        <Reveal delay={0.12}>
                            <div className="glass mt-6 rounded-2xl border border-gold-line p-6">
                                <p className="font-mono-x text-[10px] tracking-[0.3em] text-brass uppercase">
                                    {L(T.recommendation)}
                                </p>
                                <p className="mt-3 leading-relaxed text-txt-1" data-testid="compare-recommendation" dir="auto">
                                    {result.recommendation}
                                </p>
                            </div>
                        </Reveal>

                        {result.negotiation_tips?.length > 0 && (
                            <Reveal delay={0.16}>
                                <div className="mt-6 rounded-2xl border border-gold-line bg-ink-2/50 p-6">
                                    <p className="flex items-center gap-2 font-mono-x text-[10px] tracking-[0.3em] text-brass uppercase">
                                        <Lightbulb className="size-3.5" />
                                        {L(T.tips)}
                                    </p>
                                    <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-txt-2">
                                        {result.negotiation_tips.map((tip, j) => (
                                            <li key={j} dir="auto">{tip}</li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        )}

                        <Reveal delay={0.2}>
                            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                                <button
                                    onClick={() => scrollToId("contact")}
                                    data-testid="compare-cta-quote"
                                    className="rounded-full bg-brass px-7 py-3.5 text-sm font-bold text-ink transition-transform hover:scale-105"
                                >
                                    {L(T.quoteCta)}
                                </button>
                                <p className="font-mono-x text-[10px] text-txt-3" dir="auto">
                                    {usedFallback ? "— " : ""}
                                    {L(T.disclaimer)}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                )}
            </div>
        </section>
    );
}
